import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * Uses morph (DOM diffing) instead of replaceWith to do a surgical update
   * that preserves existing DOM nodes and any third-party bindings (e.g. GPO).
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Morph price container — surgical DOM diff preserves third-party
    // app bindings (e.g. GPO) that replaceWith would destroy.
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) morph(currentPrice, newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      morph(currentNote, newNote);
    }

    // Force GPO app to recalculate and re-inject its price modifications
    // after the theme has updated the base price DOM.
    // 1. If using the custom flavors picker:
    if (window.flavorsPickerInstance && typeof window.flavorsPickerInstance.updateAllSwatches === 'function') {
      window.flavorsPickerInstance.updateAllSwatches();
    } 
    // 2. Generic fallback for other GPO products: trigger a change event on any GPO input
    else {
      const gpoInput = document.querySelector('.gpo-container input[type="checkbox"], .gpo-container input[type="radio"]:checked, .gpo-container select');
      if (gpoInput) {
        gpoInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
